/**
 * GDPR-Compliant Cookie Consent Banner
 * Uses Carbon Design System Notification components with A/B Testing Support
 */

import React, { useState, useEffect } from 'react';
import {
  InlineNotification,
  NotificationActionButton,
  Button,
  Checkbox,
  Modal,
  Stack,
  FormGroup,
  Toggle,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Tile,
  ProgressIndicator,
  ProgressStep,
} from '@carbon/react';
import { Cookie, Security, Analytics, Download, TrashCan, Information } from '@carbon/icons-react';

interface ConsentPreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
  timestamp: string;
  version: string;
  visitorId?: string;
  ipHash?: string;
  abTestVariant?: string;
}

interface ABTestVariant {
  variantId: string;
  name: string;
  bannerStyle: 'minimal' | 'detailed' | 'progressive';
  buttonLayout: 'horizontal' | 'vertical' | 'compact';
  contentEmphasis: 'privacy' | 'benefits' | 'compliance';
}

interface CookieConsentProps {
  privacyPolicyVersion?: string;
  onConsentChange?: (preferences: ConsentPreferences) => void;
  enableABTesting?: boolean;
  visitorId?: string;
}

interface ConsentAnalytics {
  conversionRate: number;
  timeToDecision: number;
  variant: string;
  acceptanceRate: {
    all: number;
    necessary: number;
    partial: number;
  };
}

const CONSENT_KEY = 'sigma08_cookie_consent';
const CONSENT_VERSION = '1.0.0';

export const CookieConsent: React.FC<CookieConsentProps> = ({
  privacyPolicyVersion = CONSENT_VERSION,
  onConsentChange,
  enableABTesting = false,
  visitorId,
}) => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [currentVariant, setCurrentVariant] = useState<ABTestVariant | null>(null);
  const [startTime] = useState(Date.now());
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    personalization: false,
    timestamp: '',
    version: privacyPolicyVersion,
    visitorId,
  });

  const variants: ABTestVariant[] = [
    {
      variantId: 'control',
      name: 'Standard Banner',
      bannerStyle: 'detailed',
      buttonLayout: 'horizontal',
      contentEmphasis: 'privacy',
    },
    {
      variantId: 'minimal',
      name: 'Minimal Banner',
      bannerStyle: 'minimal',
      buttonLayout: 'compact',
      contentEmphasis: 'benefits',
    },
    {
      variantId: 'progressive',
      name: 'Progressive Disclosure',
      bannerStyle: 'progressive',
      buttonLayout: 'vertical',
      contentEmphasis: 'compliance',
    },
  ];

  // Check for existing consent on mount and initialize A/B testing
  useEffect(() => {
    const checkConsent = async () => {
      // Initialize A/B testing variant if enabled
      if (enableABTesting && !currentVariant) {
        const selectedVariant = await selectABTestVariant();
        setCurrentVariant(selectedVariant);
      }

      const stored = localStorage.getItem(CONSENT_KEY);

      if (!stored) {
        // No consent found - only show banner on marketing pages
        const isMarketingPage =
          window.location.pathname === '/' ||
          window.location.pathname.includes('/pricing') ||
          window.location.pathname.includes('/features') ||
          window.location.pathname.includes('/about') ||
          window.location.pathname.includes('/marketing');

        // Don't show on admin or dashboard pages
        const isAdminPage =
          window.location.pathname.includes('/admin') ||
          window.location.pathname.includes('/dashboard') ||
          window.location.pathname.includes('/ab-test');

        if (isMarketingPage && !isAdminPage) {
          setShowBanner(true);
          // Track banner impression for A/B testing
          if (enableABTesting && currentVariant) {
            trackConsentEvent('banner_shown', { variant: currentVariant.variantId });
          }
        }
        return;
      }

      try {
        const consent: ConsentPreferences = JSON.parse(stored);

        // Check if consent version matches current privacy policy
        if (consent.version !== privacyPolicyVersion) {
          // Privacy policy updated, request new consent
          setShowBanner(true);
          if (enableABTesting && currentVariant) {
            trackConsentEvent('banner_shown_version_update', {
              variant: currentVariant.variantId,
              oldVersion: consent.version,
              newVersion: privacyPolicyVersion,
            });
          }
          return;
        }

        // Valid consent exists
        setPreferences(consent);
        if (onConsentChange) {
          onConsentChange(consent);
        }
      } catch (error) {
        console.error('Error parsing consent:', error);
        setShowBanner(true);
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(checkConsent, 100);
  }, [privacyPolicyVersion, onConsentChange, enableABTesting, currentVariant]);

  // A/B Test Variant Selection
  const selectABTestVariant = async (): Promise<ABTestVariant> => {
    if (!visitorId) {
      return variants[0]; // Default to control
    }

    try {
      // Check for existing variant assignment
      const stored = localStorage.getItem(`ab_variant_${visitorId}`);
      if (stored) {
        const variant = variants.find(v => v.variantId === stored);
        if (variant) return variant;
      }

      // Fetch variant assignment from backend
      const response = await fetch('/api/analytics/ab-tests/consent-banner/assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visitorId }),
      });

      if (response.ok) {
        const { variantId } = await response.json();
        const variant = variants.find(v => v.variantId === variantId) || variants[0];
        localStorage.setItem(`ab_variant_${visitorId}`, variant.variantId);
        return variant;
      }
    } catch (error) {
      console.error('Error selecting A/B test variant:', error);
    }

    // Fallback to random assignment
    const randomIndex = Math.floor(Math.random() * variants.length);
    const variant = variants[randomIndex];
    if (visitorId) {
      localStorage.setItem(`ab_variant_${visitorId}`, variant.variantId);
    }
    return variant;
  };

  // Track consent events for analytics
  const trackConsentEvent = async (eventType: string, data: any) => {
    if (!visitorId) return;

    try {
      await fetch('/api/analytics/events/privacy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          events: [
            {
              visitorId,
              sessionId: `sess_${Date.now()}`,
              eventType: 'consent_interaction',
              pageUrl: window.location.href,
              timestamp: new Date().toISOString(),
              eventData: {
                consentEventType: eventType,
                timeOnPage: Date.now() - startTime,
                ...data,
              },
            },
          ],
          metadata: {
            source: 'consent_banner',
            version: privacyPolicyVersion,
          },
        }),
      });
    } catch (error) {
      console.error('Error tracking consent event:', error);
    }
  };

  const handleAcceptAll = async () => {
    const decisionTime = Date.now() - startTime;

    // Track the decision for A/B testing analytics
    if (enableABTesting && currentVariant) {
      await trackConsentEvent('accept_all', {
        variant: currentVariant.variantId,
        decisionTime,
        buttonClicked: 'accept_all',
      });
    }

    const newPreferences: ConsentPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      personalization: true,
      timestamp: new Date().toISOString(),
      version: privacyPolicyVersion,
      visitorId,
      abTestVariant: currentVariant?.variantId,
    };

    saveConsent(newPreferences);
  };

  const handleAcceptNecessary = async () => {
    const decisionTime = Date.now() - startTime;

    // Track the decision for A/B testing analytics
    if (enableABTesting && currentVariant) {
      await trackConsentEvent('accept_necessary', {
        variant: currentVariant.variantId,
        decisionTime,
        buttonClicked: 'necessary_only',
      });
    }

    const newPreferences: ConsentPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      personalization: false,
      timestamp: new Date().toISOString(),
      version: privacyPolicyVersion,
      visitorId,
      abTestVariant: currentVariant?.variantId,
    };

    saveConsent(newPreferences);
  };

  const handleSaveCustom = async () => {
    const decisionTime = Date.now() - startTime;

    // Track the decision for A/B testing analytics
    if (enableABTesting && currentVariant) {
      await trackConsentEvent('save_custom', {
        variant: currentVariant.variantId,
        decisionTime,
        buttonClicked: 'save_custom',
        selectedPreferences: {
          analytics: preferences.analytics,
          marketing: preferences.marketing,
          personalization: preferences.personalization,
        },
      });
    }

    const newPreferences: ConsentPreferences = {
      ...preferences,
      timestamp: new Date().toISOString(),
      version: privacyPolicyVersion,
      visitorId,
      abTestVariant: currentVariant?.variantId,
    };

    saveConsent(newPreferences);
    setShowDetails(false);
  };

  const saveConsent = async (consent: ConsentPreferences) => {
    // Save to localStorage for persistence
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));

    // Generate IP hash for GDPR compliance (no actual IP stored)
    const ipHash = await generateIPHash();

    // Send consent to analytics backend for GDPR compliance tracking
    try {
      await fetch('/api/analytics/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitorId: consent.visitorId || 'anonymous',
          ipHash,
          consentVersion: consent.version,
          purposes: [
            { purpose: 'ANALYTICS', granted: consent.analytics, timestamp: consent.timestamp },
            { purpose: 'MARKETING', granted: consent.marketing, timestamp: consent.timestamp },
            { purpose: 'PERSONALIZATION', granted: consent.personalization, timestamp: consent.timestamp },
            { purpose: 'NECESSARY', granted: consent.necessary, timestamp: consent.timestamp },
          ],
        }),
      });
    } catch (error) {
      console.error('Error recording consent to analytics backend:', error);
    }

    // Create audit log entry for legacy compatibility
    const auditEntry = {
      action: 'cookie_consent_updated',
      preferences: consent,
      timestamp: consent.timestamp,
      userAgent: navigator.userAgent,
      abTestVariant: consent.abTestVariant,
    };

    // Send to privacy backend for audit trail
    fetch('/api/privacy/consent-audit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(auditEntry),
    }).catch(console.error);

    // Notify parent component
    if (onConsentChange) {
      onConsentChange(consent);
    }

    // Hide banner
    setShowBanner(false);
  };

  // Generate privacy-compliant IP hash
  const generateIPHash = async (): Promise<string> => {
    try {
      // In production, this would use a more sophisticated method
      // that doesn't expose the actual IP
      const response = await fetch('/api/privacy/ip-hash', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const { hash } = await response.json();
        return hash;
      }
    } catch (error) {
      console.error('Error generating IP hash:', error);
    }

    // Fallback to timestamp-based hash for demo purposes
    return `hash_${Date.now()}`;
  };

  // Get banner content based on A/B test variant
  const getBannerContent = () => {
    if (!currentVariant) return getDefaultBannerContent();

    switch (currentVariant.contentEmphasis) {
      case 'privacy':
        return {
          title: 'We respect your privacy',
          description:
            'Your data protection is our priority. We use only essential cookies by default and ask for your consent before collecting additional data.',
          compliance: 'Compliant with GDPR, COPPA, and FERPA regulations.',
        };
      case 'benefits':
        return {
          title: 'Enhance your experience',
          description: 'Allow us to personalize your learning journey and improve our platform through analytics.',
          compliance: 'All data handling follows strict privacy regulations.',
        };
      case 'compliance':
        return {
          title: 'Data privacy consent required',
          description:
            'As required by data protection laws, we need your consent to process personal data beyond essential functionality.',
          compliance: 'Full GDPR, COPPA, and FERPA compliance ensures your rights are protected.',
        };
      default:
        return getDefaultBannerContent();
    }
  };

  const getDefaultBannerContent = () => ({
    title: 'We value your privacy',
    description: 'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content.',
    compliance: 'This site complies with GDPR, COPPA, and FERPA regulations for educational data privacy.',
  });

  if (!showBanner) {
    return (
      <>
        {/* Analytics Dashboard Access for Testing */}
        {enableABTesting && (
          <Button
            kind="ghost"
            size="sm"
            onClick={() => setShowAnalytics(true)}
            style={{
              position: 'fixed',
              bottom: '20px',
              left: '20px',
              zIndex: 8888,
            }}
          >
            View Analytics
          </Button>
        )}
        {renderAnalyticsModal()}
      </>
    );
  }

  const bannerContent = getBannerContent();

  return (
    <>
      {/* Main Cookie Consent Banner */}
      <div
        className="cookie-consent-banner"
        role="region"
        aria-label="Cookie consent"
        data-variant={currentVariant?.variantId}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          backgroundColor: 'var(--cds-layer-01)',
          borderTop: '2px solid #8a3ffc', // Galaxy Purple border
          padding: currentVariant?.bannerStyle === 'minimal' ? 'var(--cds-spacing-05)' : 'var(--cds-spacing-06)',
          boxShadow:
            currentVariant?.bannerStyle === 'detailed'
              ? '0 -4px 12px rgba(138, 63, 252, 0.15)'
              : '0 -2px 8px rgba(138, 63, 252, 0.1)',
        }}
      >
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <Stack gap={currentVariant?.bannerStyle === 'minimal' ? 3 : 5}>
            <div style={{ display: 'flex', alignItems: 'start', gap: 'var(--cds-spacing-05)' }}>
              <Cookie size={24} aria-label="Cookie icon" style={{ color: '#0f62fe' }} />
              <div style={{ flex: 1 }}>
                <h3
                  className="cds--type-heading-03"
                  style={{ color: '#0f62fe', marginBottom: 'var(--cds-spacing-03)' }}
                >
                  {bannerContent.title}
                </h3>
                <p className="cds--type-body-01" style={{ marginTop: 'var(--cds-spacing-03)' }}>
                  {bannerContent.description}
                  {currentVariant?.bannerStyle !== 'minimal' && (
                    <>
                      {' '}
                      By clicking "Accept All", you consent to our use of cookies. You can manage your preferences by
                      clicking "Manage Preferences".
                    </>
                  )}
                </p>
                {currentVariant?.bannerStyle !== 'minimal' && (
                  <p className="cds--type-body-01" style={{ marginTop: 'var(--cds-spacing-02)' }}>
                    {bannerContent.compliance}
                  </p>
                )}
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                gap: 'var(--cds-spacing-03)',
                flexDirection: currentVariant?.buttonLayout === 'vertical' ? 'column' : 'row',
                flexWrap: currentVariant?.buttonLayout === 'vertical' ? 'nowrap' : 'wrap',
                alignItems: currentVariant?.buttonLayout === 'vertical' ? 'stretch' : 'center',
              }}
            >
              <Button
                kind="primary"
                size={currentVariant?.buttonLayout === 'compact' ? 'sm' : 'md'}
                onClick={handleAcceptAll}
                aria-label="Accept all cookies"
                style={{
                  backgroundColor: '#0f62fe',
                  borderColor: '#0f62fe',
                  minWidth: '120px',
                }}
              >
                Accept All
              </Button>
              <Button
                kind="secondary"
                size={currentVariant?.buttonLayout === 'compact' ? 'sm' : 'md'}
                onClick={handleAcceptNecessary}
                aria-label="Accept necessary cookies only"
                style={{
                  borderColor: '#0f62fe',
                  color: '#0f62fe',
                  minWidth: '120px',
                }}
              >
                Necessary Only
              </Button>
              <Button
                kind="tertiary"
                size={currentVariant?.buttonLayout === 'compact' ? 'sm' : 'md'}
                onClick={() => setShowDetails(true)}
                aria-label="Manage cookie preferences"
                style={{
                  color: '#0f62fe',
                  minWidth: '140px',
                }}
              >
                Manage Preferences
              </Button>
            </div>
          </Stack>
        </div>
      </div>

      {/* Detailed Preferences Modal */}
      <Modal
        open={showDetails}
        modalHeading="Cookie Preferences"
        modalLabel="Privacy Settings"
        primaryButtonText="Save Preferences"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSaveCustom}
        onRequestClose={() => setShowDetails(false)}
        size="md"
      >
        <Stack gap={6}>
          <p className="cds--type-body-01">
            Manage your cookie preferences below. Necessary cookies are always enabled as they are required for the site
            to function properly.
          </p>

          <FormGroup legendText="Cookie Categories">
            <Stack gap={4}>
              {/* Necessary Cookies */}
              <div
                style={{
                  padding: 'var(--cds-spacing-04)',
                  backgroundColor: 'var(--cds-layer-02)',
                  borderRadius: '4px',
                }}
              >
                <Checkbox
                  id="necessary-cookies"
                  labelText="Necessary Cookies"
                  checked={true}
                  disabled={true}
                  helperText="Required for basic site functionality, authentication, and security. Cannot be disabled."
                />
                <div style={{ marginTop: 'var(--cds-spacing-02)', marginLeft: 'var(--cds-spacing-07)' }}>
                  <Security size={16} style={{ marginRight: 'var(--cds-spacing-02)' }} />
                  <span className="cds--type-helper-text-01">
                    Includes: Session management, CSRF protection, user preferences
                  </span>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div
                style={{
                  padding: 'var(--cds-spacing-04)',
                  backgroundColor: 'var(--cds-layer-02)',
                  borderRadius: '4px',
                }}
              >
                <Checkbox
                  id="analytics-cookies"
                  labelText="Analytics Cookies"
                  checked={preferences.analytics}
                  onChange={(_, { checked }) => setPreferences({ ...preferences, analytics: checked })}
                  helperText="Help us understand how visitors interact with our website to improve user experience."
                />
                <div style={{ marginTop: 'var(--cds-spacing-02)', marginLeft: 'var(--cds-spacing-07)' }}>
                  <Analytics size={16} style={{ marginRight: 'var(--cds-spacing-02)' }} />
                  <span className="cds--type-helper-text-01">
                    Includes: Page views, feature usage, performance metrics
                  </span>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div
                style={{
                  padding: 'var(--cds-spacing-04)',
                  backgroundColor: 'var(--cds-layer-02)',
                  borderRadius: '4px',
                }}
              >
                <Checkbox
                  id="marketing-cookies"
                  labelText="Marketing Cookies"
                  checked={preferences.marketing}
                  onChange={(_, { checked }) => setPreferences({ ...preferences, marketing: checked })}
                  helperText="Used to track visitors across websites to display relevant advertisements."
                />
                <div style={{ marginTop: 'var(--cds-spacing-02)', marginLeft: 'var(--cds-spacing-07)' }}>
                  <span className="cds--type-helper-text-01">
                    Includes: Ad personalization, conversion tracking, remarketing
                  </span>
                </div>
              </div>

              {/* Personalization Cookies */}
              <div
                style={{
                  padding: 'var(--cds-spacing-04)',
                  backgroundColor: 'var(--cds-layer-02)',
                  borderRadius: '4px',
                }}
              >
                <Checkbox
                  id="personalization-cookies"
                  labelText="Personalization Cookies"
                  checked={preferences.personalization}
                  onChange={(_, { checked }) => setPreferences({ ...preferences, personalization: checked })}
                  helperText="Personalize content and recommendations based on your learning preferences."
                />
                <div style={{ marginTop: 'var(--cds-spacing-02)', marginLeft: 'var(--cds-spacing-07)' }}>
                  <span className="cds--type-helper-text-01">
                    Includes: Content recommendations, AI tutoring, learning path optimization
                  </span>
                </div>
              </div>
            </Stack>
          </FormGroup>

          <div
            style={{
              padding: 'var(--cds-spacing-03)',
              backgroundColor: 'var(--cds-layer-accent)',
              borderRadius: '4px',
            }}
          >
            <p className="cds--type-helper-text-01">
              <strong>Privacy Policy Version:</strong> {privacyPolicyVersion}
              <br />
              <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
              <br />
              You can withdraw or change your consent at any time from your Privacy Dashboard.
            </p>
          </div>
        </Stack>
      </Modal>

      {renderAnalyticsModal()}
    </>
  );

  // Render Analytics Dashboard Modal for A/B Testing Insights
  function renderAnalyticsModal() {
    if (!enableABTesting) return null;

    return (
      <Modal
        open={showAnalytics}
        modalHeading="Consent Banner Analytics"
        modalLabel="A/B Testing Dashboard"
        primaryButtonText="Close"
        size="lg"
        onRequestClose={() => setShowAnalytics(false)}
        onRequestSubmit={() => setShowAnalytics(false)}
      >
        <Stack gap={6}>
          {/* Current Test Information */}
          <Tile>
            <Stack gap={4}>
              <h4 className="cds--type-productive-heading-02">Current A/B Test</h4>
              {currentVariant && (
                <div>
                  <Tag type="blue" size="sm">
                    {currentVariant.name}
                  </Tag>
                  <p className="cds--type-body-01" style={{ marginTop: 'var(--cds-spacing-02)' }}>
                    <strong>Variant:</strong> {currentVariant.variantId}
                    <br />
                    <strong>Style:</strong> {currentVariant.bannerStyle}
                    <br />
                    <strong>Layout:</strong> {currentVariant.buttonLayout}
                    <br />
                    <strong>Emphasis:</strong> {currentVariant.contentEmphasis}
                  </p>
                </div>
              )}
            </Stack>
          </Tile>

          {/* Test Variants */}
          <div>
            <h4 className="cds--type-productive-heading-02">Test Variants</h4>
            <DataTable
              rows={variants.map((variant, index) => ({
                id: variant.variantId,
                name: variant.name,
                style: variant.bannerStyle,
                layout: variant.buttonLayout,
                emphasis: variant.contentEmphasis,
                isActive: currentVariant?.variantId === variant.variantId,
              }))}
              headers={[
                { key: 'name', header: 'Variant Name' },
                { key: 'style', header: 'Banner Style' },
                { key: 'layout', header: 'Button Layout' },
                { key: 'emphasis', header: 'Content Emphasis' },
                { key: 'isActive', header: 'Active' },
              ]}
              render={({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                <TableContainer>
                  <Table {...getTableProps()}>
                    <TableHead>
                      <TableRow>
                        {headers.map(header => (
                          <TableHeader {...getHeaderProps({ header })} key={header.key}>
                            {header.header}
                          </TableHeader>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map(row => (
                        <TableRow {...getRowProps({ row })} key={row.id}>
                          {row.cells.map(cell => {
                            if (cell.info.header === 'isActive') {
                              return (
                                <TableCell key={cell.id}>
                                  {cell.value ? (
                                    <Tag type="green" size="sm">
                                      Active
                                    </Tag>
                                  ) : (
                                    <Tag type="gray" size="sm">
                                      Inactive
                                    </Tag>
                                  )}
                                </TableCell>
                              );
                            }
                            return <TableCell key={cell.id}>{cell.value}</TableCell>;
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            />
          </div>

          {/* Analytics Information */}
          <Tile>
            <Stack gap={4}>
              <h4 className="cds--type-productive-heading-02">Analytics Integration</h4>
              <p className="cds--type-body-01">
                This consent banner integrates with the privacy-compliant analytics system to track:
              </p>
              <ul style={{ marginLeft: 'var(--cds-spacing-05)' }}>
                <li>Banner impression events</li>
                <li>User interaction timing</li>
                <li>Consent decision tracking</li>
                <li>Variant performance metrics</li>
                <li>GDPR compliance monitoring</li>
              </ul>
              <p className="cds--type-helper-text-01">
                All tracking respects user privacy preferences and follows GDPR requirements.
              </p>
            </Stack>
          </Tile>
        </Stack>
      </Modal>
    );
  }
};

export default CookieConsent;
